import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import apiService from '../services/api';
import WebServicesOutageFormDialog from '../components/WebServicesOutageFormDialog';
import TimelineManagement from '../components/TimelineManagement';

interface WebServicesOutage {
  id: string;
  incident_id: string;
  title: string;
  description: string | null;
  product: string | null;
  status: string;
  severity: string;
  start_time: string | null;
  resolved_at: string | null;
  estimated_resolution: string | null;
  created_at: string;
  updated_at: string;
  created_by_username: string | null;
  updated_by_username: string | null;
}

const PRODUCT_OPTIONS = [
  'Domains',
  'Web Hosting',
  'Virtual Machines',
  'Acronis',
  'Pallisade',
  'Sonic Webmail - sonicmail.com.au',
  'Sonic Webmail - sonicmail.net.au',
  'Google Workspace',
  'Microsoft 365',
];

const STATUS_OPTIONS = ['planned', 'active', 'in_progress', 'resolved'];

const WebServicesOutagesPage: React.FC = () => {
  const [outages, setOutages] = useState<WebServicesOutage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  
  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOutage, setSelectedOutage] = useState<WebServicesOutage | null>(null);
  const [selectedOutageDetails, setSelectedOutageDetails] = useState<any>(null);

  const fetchOutages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (productFilter !== 'all') params.product = productFilter;

      const data = await apiService.getWebServicesOutages(params);
      setOutages(data.outages || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch web services outages');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, productFilter]);

  useEffect(() => {
    fetchOutages();
  }, [fetchOutages]);

  const handleCreate = () => {
    setSelectedOutage(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (outage: WebServicesOutage) => {
    setSelectedOutage(outage);
    setFormDialogOpen(true);
  };

  const handleDelete = async (outage: WebServicesOutage) => {
    if (!window.confirm(`Are you sure you want to delete incident ${outage.incident_id}?`)) {
      return;
    }
    
    try {
      await apiService.deleteWebServicesOutage(outage.id);
      fetchOutages();
    } catch (err: any) {
      alert(`Failed to delete outage: ${err.message}`);
    }
  };

  const handleView = async (outage: WebServicesOutage) => {
    try {
      const details = await apiService.getWebServicesOutage(outage.id);
      const timeline = await apiService.getWebServicesOutageTimeline(outage.id);
      setSelectedOutageDetails({ ...details, timeline });
      setSelectedOutage(outage);
      setViewDialogOpen(true);
    } catch (err: any) {
      alert(`Failed to load outage details: ${err.message}`);
    }
  };

  const handleTimeline = async (outage: WebServicesOutage) => {
    try {
      const details = await apiService.getWebServicesOutage(outage.id);
      const timeline = await apiService.getWebServicesOutageTimeline(outage.id);
      setSelectedOutageDetails({ ...details, timeline });
      setSelectedOutage(outage);
      setTimelineDialogOpen(true);
    } catch (err: any) {
      alert(`Failed to load timeline: ${err.message}`);
    }
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setSelectedOutage(null);
    fetchOutages();
  };

  const handleTimelineClose = () => {
    setTimelineDialogOpen(false);
    setSelectedOutage(null);
    setSelectedOutageDetails(null);
    fetchOutages();
  };

  const handleViewClose = () => {
    setViewDialogOpen(false);
    setSelectedOutage(null);
    setSelectedOutageDetails(null);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const aestDate = utcToZonedTime(date, 'Australia/Sydney');
      return format(aestDate, 'dd/MM/yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'success';
      case 'active':
        return 'error';
      case 'in_progress':
        return 'warning';
      case 'planned':
        return 'info';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Web Services Outages
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Incident
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: false }}
            >
              <MenuItem value="all">All</MenuItem>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Product"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              SelectProps={{ native: false }}
            >
              <MenuItem value="all">All</MenuItem>
              {PRODUCT_OPTIONS.map((product) => (
                <MenuItem key={product} value={product}>
                  {product}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Incident ID</strong></TableCell>
                <TableCell><strong>Title</strong></TableCell>
                <TableCell><strong>Product</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Severity</strong></TableCell>
                <TableCell><strong>Start Time</strong></TableCell>
                <TableCell><strong>End Time</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No web services outages found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                outages.map((outage) => (
                  <TableRow key={outage.id} hover>
                    <TableCell>{outage.incident_id}</TableCell>
                    <TableCell>{outage.title}</TableCell>
                    <TableCell>{outage.product || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={outage.status.charAt(0).toUpperCase() + outage.status.slice(1).replace('_', ' ')}
                        color={getStatusColor(outage.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={outage.severity}
                        color={getSeverityColor(outage.severity) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(outage.start_time)}</TableCell>
                    <TableCell>{formatDateTime(outage.resolved_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(outage)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(outage)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Timeline">
                          <IconButton size="small" onClick={() => handleTimeline(outage)}>
                            <TimelineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(outage)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Form Dialog */}
      <WebServicesOutageFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        outage={selectedOutage}
      />

      {/* Timeline Management Dialog */}
      <TimelineManagement
        open={timelineDialogOpen}
        onClose={handleTimelineClose}
        outage={selectedOutage}
        outageDetails={selectedOutageDetails}
        outageType="webservices"
      />

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleViewClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedOutageDetails?.incident_id || selectedOutage?.incident_id}
            </Typography>
            {selectedOutageDetails && (
              <Chip
                label={selectedOutageDetails.status.charAt(0).toUpperCase() + selectedOutageDetails.status.slice(1).replace('_', ' ')}
                color={getStatusColor(selectedOutageDetails.status) as any}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOutageDetails && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography variant="body1">{selectedOutageDetails.title}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{selectedOutageDetails.description || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Product</Typography>
                <Typography variant="body1">{selectedOutageDetails.product || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Severity</Typography>
                <Chip
                  label={selectedOutageDetails.severity}
                  color={getSeverityColor(selectedOutageDetails.severity) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedOutageDetails.status.charAt(0).toUpperCase() + selectedOutageDetails.status.slice(1).replace('_', ' ')}
                  color={getStatusColor(selectedOutageDetails.status) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Start Time</Typography>
                <Typography variant="body1">{formatDateTime(selectedOutageDetails.start_time)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">End Time</Typography>
                <Typography variant="body1">{formatDateTime(selectedOutageDetails.resolved_at)}</Typography>
              </Grid>
              {selectedOutageDetails.timeline && selectedOutageDetails.timeline.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom>Timeline</Typography>
                  </Grid>
                  {selectedOutageDetails.timeline.map((entry: any, index: number) => (
                    <Grid item xs={12} key={index}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="start">
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary">
                                {formatDateTime(entry.timestamp)}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {entry.description}
                              </Typography>
                              <Chip
                                label={entry.entry_type}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebServicesOutagesPage;

